import { arrayToOrderedMap } from './utils'
import * as R from 'ramda'

import { initialState, types } from 'store/Applicant'
import { siftApplicants } from 'utils/applicants'

export const setApplicants = applicants => ({
  type: types.SET_APPLICANTS,
  payload: applicants,
})

// setting tempId here as default so that we can pass it in for tests
export const addApplicant = (
  applicant = {},
  tempId = new Date().toISOString().replace('.', ':'),
) => ({
  type: types.ADD_APPLICANT,
  payload: applicant,
  meta: { tempId },
})

export const updateApplicant = applicant => ({
  type: types.UPDATE_APPLICANT,
  payload: applicant,
})

export const removeApplicant = uuid => ({
  type: types.REMOVE_APPLICANT,
  payload: uuid,
})

export const clearApplicants = () => ({
  type: types.CLEAR_APPLICANTS,
})

export default (
  state = initialState,
  { type, payload = {}, meta = {} } = {},
) => {
  switch (type) {
    case types.SET_APPLICANTS: {
      const applicants = arrayToOrderedMap('uuid')(payload)
      const nextState = R.assocPath(
        ['applicants', 'entities'],
        applicants,
        state,
      )
      return siftApplicants(nextState)
    }

    // ----------

    // TODO IDK if this works. Kinda eyeballed it.
    case types.ADD_APPLICANT: {
      const applicants = R.pathOr({}, ['applicants', 'entities'], state)
      const hasApplicants = Object.keys(applicants).length > 0

      // make sure the new applicant has a uuid
      if (!payload.uuid) payload.uuid = meta.tempId

      // new applicants will always be pending purchase
      payload.enrollmentStatus = 'PENDING_PURCHASE'

      // use provided data for primary related properties when provided
      // otherwise, run some logic to determine if the new applicant is
      // a primary or dependent.
      if (R.isNil(payload.primary)) {
        // if this is the first applicant, make them primary; all other
        // members will be considered dependents.
        if (!hasApplicants) {
          payload.primary = true
          payload.relationWithPrimary = 'PRIMARY'
        } else {
          payload.primary = false
          payload.relationWithPrimary = 'DEPENDENT'

          // we need to copy the address and the quote/startDate from the primary
          // because we'll need those to be available in every member and it's
          // a safe assumption they'll be the same anyway.
          const primaryApplicant = R.find(
            R.propEq('primary', true),
            R.values(applicants),
          )
          if (primaryApplicant) {
            payload.mailingAddress = primaryApplicant.mailingAddress
            payload.quote = primaryApplicant.quote
          }
        }
      }

      // do not allow dual primaries
      if (
        payload.primary &&
        R.find(R.propEq('primary', true), R.values(applicants))
      ) {
        payload.primary = false
      }

      const nextState = R.assocPath(
        ['applicants', 'entities', payload.uuid],
        payload,
        state,
      )
      return siftApplicants(nextState)
    }

    // ----------

    case types.UPDATE_APPLICANT: {
      const applicants = R.pathOr({}, ['applicants', 'entities'], state)

      // do not allow dual primaries
      if (
        payload.primary &&
        R.find(a => a.primary && a.uuid !== payload.uuid, R.values(applicants))
      ) {
        payload.primary = false
      }

      const nextState = R.mergeDeepRight(state, {
        applicants: { entities: { [payload.uuid]: payload } },
      })

      return siftApplicants(nextState)
    }

    // ----------

    case types.REMOVE_APPLICANT: {
      const isPrimary = R.path(
        ['applicants', 'entities', payload, 'primary'],
        state,
      )

      // we don't want to ever delete the primary
      if (isPrimary) return state

      const nextState = R.assocPath(
        ['applicants', 'entities'],
        R.omit([payload], R.path(['applicants', 'entities'], state)),
        state,
      )

      return siftApplicants(nextState)
    }

    // ----------

    case types.CLEAR_APPLICANTS: {
      return R.assocPath(['applicants', 'entities'], {}, state)
    }

    // ----------

    default:
      return state
  }
}
